import {
	alpha,
	Backdrop,
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	Chip,
	CircularProgress,
	IconButton,
	ListItem,
	Modal,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useMemo, useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ASSETS_BASE_URL, SERVER_BASE_URL } from "../../config";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { useLocation, useNavigate } from "react-router-dom";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import { useTranslation } from "react-i18next";
import { processModel } from "../../services/multipleModelViewer";
import { findModelsContainingPart, verifyResourceExists } from "../../services/modelService";
import Swal from "sweetalert2";
import ModelPDFIcon from "../ModelPDFIcon/ModelPDFIcon";
import ModelIcon from "../ModelIcon/ModelIcon";
import MachineCard from "../MachineCard/MachineCard";
import { trackSearchPerformed } from "../../utilities/analyticsService";

function extractFilename(path) {
	if (!path) return "";
	const parts = String(path).split("/");
	const lastPart = parts[parts.length - 1] || "";
	return lastPart.replace(/\.[^/.]+$/, "");
}

function normalize3DFile(file) {
	if (!file) {
		return { glb: null, html: null, htmlV2: null, json: null };
	}
	if (typeof file === "string") {
		return { glb: null, html: file, htmlV2: null, json: null };
	}
	return {
		glb: file.glb || null,
		html: file.html || null,
		htmlV2: file.htmlV2 || null,
		json: file.json || null,
	};
}

function modelToMachineCardProduct(model, machine) {
	const relThumb =
		typeof model.thumb === "string"
			? model.thumb.replace(ASSETS_BASE_URL + "/", "").replace(/^\/+/, "")
			: null;
	return {
		machine_id: machine.machine_id,
		machine_name: model.filename,
		machine_number: machine.machine_number,
		machine_product_name: model.filename,

		product_numbers: machine.productNumbers || [],
		bom_part_count: 1,

		//   Force exact model selection
		forcedModel: model,
		disableMachineImageFallback: true,
		//   Minimal model_data so MachineCard shows icon
		model_data: {
			...(relThumb ? { images: [relThumb], all_images: [relThumb] } : {}),
			"3dfile": model.modelType === "3D"
				? model.filepath.replace(SERVER_BASE_URL, "")
				: null,
			"2dfile": model.modelType === "2D"
				? model.filepath.replace(SERVER_BASE_URL, "")
				: null,
			splitfiles: [],
			boms: [],
		},
	};
}


/**
 * DOM id for a model card so the modal can scroll the first matching card into view.
 *
 * @param {Object} model Decorated model entry.
 * @returns {string} Stable id built from type, 2D/3D and filename.
 */
const cardDomId = (model) =>
	`model-card-${String(`${model.type}-${model.modelType}-${model.filename}`).replace(/[^A-Za-z0-9_-]+/g, "-")}`;

/**
 * Modal listing every model a machine has (machine model, split files, sub-assembly models,
 * 2D drawings) so the user can pick one to open.
 *
 * When opened from a part page, models that contain that part are badged "Has Part#: <number>",
 * sorted first in their section, and their section is expanded. Sub-assembly models are matched
 * from `highlightProductNumbers` (the part and its BOM ancestors, computed by the part page);
 * GLB models are matched by asking the server to scan their BOM JSON.
 *
 * @param {Object} props
 * @param {boolean} props.show Whether the modal is open.
 * @param {Function} props.setShow Setter to close it.
 * @param {string} props.machine_number Machine number the models belong to.
 * @param {string} props.machine_name Machine display name.
 * @param {Array<string>} props.productNumbers The current part number in a one-element array (empty on the machine page).
 * @param {number|string} props.machine_id Machine id for viewer routes.
 * @param {string} [props.machine_product_name] Unused legacy label.
 * @param {boolean} [props.has2D=false] Whether the machine has a 2D drawing.
 * @param {Object} [props.modelData] The machine's `model_data` from assets_cache.
 * @param {Array<string>} [props.highlightProductNumbers] Product numbers of the current part and its BOM ancestors.
 * @returns {JSX.Element}
 */
const MultipleModelViewer = ({
	show,
	setShow,
	machine_number,
	machine_name,
	productNumbers = [],
	machine_id,
	machine_product_name = "",
	has2D = false,
	modelData = {},
	highlightProductNumbers = []
}) => {
	const [models, setModels] = useState([]);
	// filename -> true | false | null (null: no BOM JSON beside that GLB, so unknown)
	const [modelsContainingPart, setModelsContainingPart] = useState({});
	const partNumber = Array.isArray(productNumbers) && productNumbers.length ? String(productNumbers[0]) : "";
	const [loading, setLoading] = useState(false);
	const { user } = useAuth();
	const { selectedSite, selectedSiteCurrency, selectedSiteData } = useSite();
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const { t } = useTranslation();
	const returnPath = `${location.pathname}${location.search}${location.hash}`;

	const default_img = `${ASSETS_BASE_URL}/stock.png`;

	// 🔸 Base machine-level image (for fallback)
	const machineBaseThumb = (() => {
		const md = modelData || {};
		const img =
			(Array.isArray(md.images) && md.images[0]) ||
			(Array.isArray(md.all_images) && md.all_images[0]) || default_img;
		return img || null;
	})();

	// 🔸 Convert a thumb value to a fully qualified URL
	const resolveThumbSrc = (thumb) => {
		const value = thumb || null;
		if (!value) return default_img;

		if (typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"))) {
			return value;
		}

		// relative asset path like "MachineImages/1000-1004.png" or "ProductImages/..."
		return `${ASSETS_BASE_URL}/${String(value).replace(/^\/+/, "")}`;
	};

	const [searchTerm, setSearchTerm] = React.useState("");
	const [sortOption, setSortOption] = React.useState("Bom Level");
	const lastTrackedSearchRef = React.useRef("");

	const style = (theme) => ({
		position: "absolute",
		top: "50%",
		left: "50%",
		width: "80vw",
		height: "90vh",
		maxHeight: "90vh",
		transform: "translate(-50%, -50%)",
		bgcolor: alpha(theme.palette.background.paper, 1),
		boxShadow: 24,
		borderRadius: 2,
		p: 2,
		px: 2,
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-start",
		alignItems: "center",
		overflow: "hidden",
		outline: 0,
	});

	const handleCloseDialog = () => {
		setShow(false);
		setExpandedPanels({
			machine: machineModels.length > 0,
			split: machineModels.length === 0 && splitModels.length > 0,
			bom: machineModels.length === 0 && splitModels.length === 0 && bomModels.length > 0
		});
	};

	const filterAndSortModels = (models) => {
		let filtered = models;
		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter((m) =>
				m.filename.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		// Sort based on selected option
		if (sortOption === "Filename (A-Z)") {
			filtered = [...filtered].sort((a, b) => a.filename.localeCompare(b.filename));
		} else if (sortOption === "Filename (Z-A)") {
			filtered = [...filtered].sort((a, b) => b.filename.localeCompare(a.filename));
		} else if (sortOption === "Model Type") {
			filtered = [...filtered].sort((a, b) => a.modelType.localeCompare(b.modelType));
		} else if (sortOption === "Bom Level") {
			filtered = [...filtered].sort((a, b) => {
				const aLevel = parseInt(a.bom_level) || 0;
				const bLevel = parseInt(b.bom_level) || 0;
				return aLevel - bLevel;
			});
		}

		return filtered;
	};

	/**
	 * Secondary lines under a model card: its kind, parent sub-assembly, BOM level, and a
	 * "Has Part#: <number>" badge when the card holds the part the user came from.
	 *
	 * @param {Object} item Decorated model entry (see filteredAndSortedModels).
	 * @param {Function} t i18n translate function.
	 * @returns {JSX.Element}
	 */
	function renderModelSubtitle(item, t) {
		return (
			<>
				<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
					{item.type === "machine"
						? t("UI_TEXT.MACHINE_MODEL")
						: item.type === "split"
							? t("UI_TEXT.SPLIT_FILE")
							: "Subassembly"}
				</Typography>
				{item.containsPart && (
					<Chip
						size="small"
						color="primary"
						variant="outlined"
						label={t("UI_TEXT.HAS_PART_NUMBER", { defaultValue: "Has Part#: {{partNumber}}", partNumber })}
						sx={{ mt: 1, mb: 0.5, height: 20, fontSize: "11px" }}
					/>
				)}

				{item.parent_product_number && item.parent_product_number !== "N/A" && (
					<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
						<b>{t("UI_TEXT.PARENT")}</b> {item.parent_product_number}
					</Typography>
				)}

				{item.bom_level && item.bom_level !== "N/A" && (
					<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
						<b>{t("UI_TEXT.LEVEL")}</b> {item.bom_level}
					</Typography>
				)}
			</>
		);
	}


	useEffect(() => {
		if (!show || !machine_number) return;

		const models = [];

		// ---------------------------
		// MACHINE MODELS
		// ---------------------------
		const main3d = normalize3DFile(modelData["3dfile"]);

		if (main3d.htmlV2 || main3d.glb || main3d.html) {
			const inhouse = Boolean(main3d.htmlV2);
			const path = main3d.htmlV2 || main3d.glb || main3d.html;
			models.push({
				filename: extractFilename(path) || machine_number,
				filepath: SERVER_BASE_URL + path,
				type: "machine",
				modelType: "3D",
				format: main3d.glb && !inhouse ? "glb" : "html",
				...(inhouse ? { engine: "inhouse" } : {}),
				thumb: machineBaseThumb,
			});
		}

		if (modelData["2dfile"]) {
			models.push({
				filename: extractFilename(modelData["2dfile"]) || machine_number,
				filepath: SERVER_BASE_URL + modelData["2dfile"],
				type: "machine",
				modelType: "2D",
				format: "2d",
				thumb: machineBaseThumb,
			});
		}

		// ---------------------------
		// SPLIT FILES (GLB → HTML)
		// ---------------------------
		(modelData.splitfiles || []).forEach((sf, idx) => {
			const glb = sf?.glb || null;
			const html = sf?.html || sf?.file || null;
			const htmlV2 = sf?.htmlV2 || null;
			const inhouse = Boolean(htmlV2);
			const path = htmlV2 || glb || html;
			if (!path) return;

			models.push({
				filename: extractFilename(path) || `${machine_number}_split_${idx + 1}`,
				filepath: SERVER_BASE_URL + path,
				type: "split",
				modelType: "3D",
				format: glb && !inhouse ? "glb" : "html",
				...(inhouse ? { engine: "inhouse" } : {}),
				thumb: sf?.image?.[0] || null,
			});
		});

		// ---------------------------
		// BOM MODELS
		// ---------------------------
		(modelData.boms || []).forEach((bom) => {
			const threeD = normalize3DFile(bom["3dfile"]);

			if (threeD.htmlV2 || threeD.glb || threeD.html) {
				const inhouse = Boolean(threeD.htmlV2);
				const path = threeD.htmlV2 || threeD.glb || threeD.html;
				models.push({
					filename: bom.product_number || extractFilename(path),
					filepath: SERVER_BASE_URL + path,
					type: "bom",
					modelType: "3D",
					format: threeD.glb && !inhouse ? "glb" : "html",
					...(inhouse ? { engine: "inhouse" } : {}),
					parent_product_number: bom.parent_product_number,
					bom_level: bom.bom_level,
					thumb: bom?.image?.[0] || null,
				});
			}

			if (bom["2dfile"]) {
				models.push({
					filename: bom.product_number || extractFilename(bom["2dfile"]),
					filepath: SERVER_BASE_URL + bom["2dfile"],
					type: "bom",
					modelType: "2D",
					format: "2d",
					parent_product_number: bom.parent_product_number,
					bom_level: bom.bom_level,
					thumb: bom?.image?.[0] || null,
				});
			}
		});

		const dedupedModels = Array.from(
			new Map(
				models.map((item) => [
					`${item.modelType}|${item.format}|${String(item.filepath || "").toLowerCase()}`,
					item,
				])
			).values()
		);

		setModels(dedupedModels);
	}, [show, machine_number, modelData, machineBaseThumb]);

	// Ask the server which GLB models contain the current part. Keyed on the part number string,
	// not the productNumbers array, because the part page rebuilds that array every render.
	useEffect(() => {
		if (!show || !partNumber || models.length === 0) {
			setModelsContainingPart({});
			return undefined;
		}
		const filenames = [...new Set(models.filter((m) => m.format === "glb").map((m) => m.filename))];
		if (!filenames.length) {
			setModelsContainingPart({});
			return undefined;
		}
		let cancelled = false;
		findModelsContainingPart({ product_number: partNumber, filenames }).then((response) => {
			if (!cancelled) setModelsContainingPart(response?.status && response?.data ? response.data : {});
		});
		return () => {
			cancelled = true;
		};
	}, [show, partNumber, models]);


	const [loadingItem, setLoadingItem] = useState(null);

	const showModelError = () => {
		Swal.fire({
			icon: "error",
			title: t('ALERT_MESSAGES.ERROR'),
			text: t('ALERT_MESSAGES.MODEL_OPEN_FAILED'),
		});
	};

	const openUrlIfExists = async (url) => {
		if (!url) {
			showModelError();
			return false;
		}
		const exists = await verifyResourceExists(url);
		if (!exists) {
			showModelError();
			return false;
		}
		window.open(url);
		return true;
	};

	/**
	 * Per-item behaviour:
	 * - If clicked item is 2D with a direct filepath → just open it (old flow).
	 * - If clicked item is 3D and filepath is GLB → /model-viewer/:machine_id/:filename (new flow).
	 * - If clicked item is 3D and only HTML → processModel (old backend flow).
	 */
	const handleModelOpen = async (item) => {
		setLoading(true);
		setLoadingItem(item?.filename || null);

		try {
			// ---------------------------
			// 1️⃣ 3D GLB → NEW VIEWER
			// ---------------------------
			if (item.modelType === "3D" && item.format === "glb") {
				navigate(`/model-viewer/${machine_id}/${item.filename}`, {
					state: { fromPath: returnPath },
				});
				return;
			}

			// ---------------------------
			// 2️⃣ 2D → DIRECT OPEN
			// ---------------------------
			if (item.modelType === "2D") {
				await openUrlIfExists(item.filepath);
				return;
			}

			// ---------------------------
			// 3️⃣ 3D HTML → BACKEND
			// ---------------------------
			const response = await processModel(
				"/models/process-modelfile",
				item.filename,
				machine_name,
				selectedSiteCurrency.toUpperCase(),
				user.user_id,
				productNumbers,
				machine_id,
				theme.palette.primary.main,
				selectedSite,
				machine_number,
				item.engine === "inhouse" ? "inhouse" : undefined,
				selectedSiteData?.site_id,
				selectedSiteData?.cus_id || selectedSiteData?.customer_id
			);

			if (response?.data?.url) {
				const modelUrl = SERVER_BASE_URL + response.data.url;
				if (item.engine === "inhouse" || response.data.kind === "inhouse") {
					if (!(await verifyResourceExists(modelUrl))) {
						showModelError();
						return;
					}
					navigate("/html-model-viewer/" + machine_id + "/" + extractFilename(response.data.url), {
						state: { modelUrl, fromPath: returnPath, machineName: machine_name, machineNumber: machine_number, kind: "inhouse" },
					});
				} else {
					await openUrlIfExists(modelUrl);
				}
			} else {
				showModelError();
			}
		} catch (err) {
			console.error(err);
			showModelError();
		} finally {
			setLoading(false);
			setLoadingItem(null);
		}
	};


	const handleSearchChange = (value) => {
		setSearchTerm(value.toLowerCase());
	};

	const highlightSet = useMemo(
		() => new Set((highlightProductNumbers || []).map((n) => String(n || "").trim().toUpperCase())),
		[highlightProductNumbers]
	);
	/**
	 * Whether a model card should be badged as containing the current part: sub-assembly models
	 * match when their product number is the part or one of its BOM ancestors; GLB models match
	 * when the server found the part in their BOM JSON.
	 *
	 * @param {Object} model Entry from the `models` state.
	 * @returns {boolean}
	 */
	const modelContainsPart = (model) =>
		(model.type === "bom" && highlightSet.has(String(model.filename || "").trim().toUpperCase())) ||
		modelsContainingPart[model.filename] === true;
	// Filter and sort all models first (models holding the current part float to the top), then group by type
	const filteredAndSortedModels = filterAndSortModels(models)
		.map((model) => ({ ...model, containsPart: modelContainsPart(model) }))
		.sort((a, b) => Number(b.containsPart) - Number(a.containsPart));
	React.useEffect(() => {
		const term = String(searchTerm || "").trim();
		if (!term) return;
		const trackingKey = `${term.toLowerCase()}|${machine_id}|${filteredAndSortedModels.length}`;
		if (lastTrackedSearchRef.current === trackingKey) return;
		lastTrackedSearchRef.current = trackingKey;
		trackSearchPerformed({
			searchTerm: term,
			searchSource: "3d_model_files",
			pageName: "3D Model",
			resultCount: filteredAndSortedModels.length,
			machine_id,
			machine_number,
		});
	}, [filteredAndSortedModels.length, machine_id, machine_number, searchTerm]);
	const machineModels = filteredAndSortedModels.filter(m => m.type === "machine");
	const splitModels = filteredAndSortedModels.filter(m => m.type === "split");
	const bomModels = filteredAndSortedModels.filter(m => m.type === "bom" || m.type === "bom_split");

	// Opened for one specific part (productNumbers is only ever non-empty from
	// SingleProduct.js's part detail page — Products.js's machine page always passes
	// []) rather than the whole machine — that part's own model normally lives in the
	// Bill of Materials/subassembly group, so prioritize expanding that instead of
	// defaulting to Machine (which otherwise wins whenever a main machine model also
	// exists, leaving the actually-relevant BOM section collapsed).
	const isPartScoped = productNumbers.length > 0;

	// Update expanded panels when models change
	const [expandedPanels, setExpandedPanels] = useState({
		machine: !isPartScoped && machineModels.length > 0,
		split: !isPartScoped && splitModels.length > 0 && machineModels.length === 0,
		bom: isPartScoped ? bomModels.length > 0 : (bomModels.length > 0 && machineModels.length === 0 && splitModels.length === 0)
	});

	const splitHasPart = splitModels.some((m) => m.containsPart);
	const bomHasPart = bomModels.some((m) => m.containsPart);
	const machineHasPart = machineModels.some((m) => m.containsPart);
	useEffect(() => {
		setExpandedPanels(
			isPartScoped
				? { machine: machineHasPart, split: splitHasPart, bom: bomModels.length > 0 }
				: {
					machine: machineModels.length > 0,
					split: (machineModels.length === 0 && splitModels.length > 0) || splitHasPart,
					bom: (machineModels.length === 0 && splitModels.length === 0 && bomModels.length > 0) || bomHasPart
				}
		);
	}, [machineModels.length, splitModels.length, bomModels.length, isPartScoped, machineHasPart, splitHasPart, bomHasPart]);

	// Scroll the first model that contains the current part into view once its section has expanded.
	const firstMatchedCardId = (() => {
		const first = filteredAndSortedModels.find((m) => m.containsPart);
		return first ? cardDomId(first) : "";
	})();
	useEffect(() => {
		if (!show || !firstMatchedCardId) return undefined;
		const timer = setTimeout(() => {
			document.getElementById(firstMatchedCardId)?.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 400);
		return () => clearTimeout(timer);
	}, [show, firstMatchedCardId]);

	const handleAccordionChange = (panel) => (event, isExpanded) => {
		setExpandedPanels((prev) => ({
			...prev,
			[panel]: isExpanded,
		}));
	};

	return (
		<Modal
			open={show}
			onClose={(e) => {
				e.stopPropagation();
				handleCloseDialog();
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description">
			<Box sx={style}>
				<Backdrop
					sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
					open={loading}
				>
					<CircularProgress color="inherit" />
				</Backdrop>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
						mt: 1,
						width: "100%",
						px: 1,
						flexShrink: 0
					}}>
					<Box>
						<Typography variant="h5" sx={{ m: 0, fontWeight: "bold" }}>
							{t('UI_TEXT.CHOOSE_A_MODEL')}{" "}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							<b>{machine_name}</b> machine has <b>multiple models</b> available. Please select one to
							continue.
						</Typography>
					</Box>

					<IconButton
						aria-label="close"
						onClick={(e) => {
							e.stopPropagation();
							handleCloseDialog();
						}}>
						<CloseIcon />
					</IconButton>
				</Box>

				<Box sx={{ mt: 1, mb: 1, width: '100%', px: 2 }}>
					<ContextFilterAndSort
						options={["Bom Level", "Filename (A-Z)", "Filename (Z-A)", "Model Type"]}
						selectedOption={sortOption}
						onSortChange={setSortOption}
						placeholder={"Search Models"}
						searchValue={searchTerm}
						onSearchChange={handleSearchChange}
						showSort={true}
						showSearch={true}
					/>
				</Box>

				{/* Scrollable Content Area */}
				<Box
					sx={{
						flex: 1,
						px: 1,
						width: "100%",
						overflowY: "auto",
						overflowX: "hidden",
					}}>
					<Accordion
						sx={{
							mt: 2, width: "100%",
							backgroundColor: alpha(theme.palette.background.paper, 1),
							borderRadius: 2,
							boxShadow: "none",
						}}
						expanded={expandedPanels.machine}
						onChange={handleAccordionChange("machine")}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}
							sx={{
								px: 2,
								pr: 2,
								...(expandedPanels.bom && {
									position: "sticky",
									top: 0,
									zIndex: 3,
									backgroundColor: alpha(theme.palette.background.paper, 1),
								})
							}}
						>
							<Typography variant="h6" sx={{ fontWeight: "bold" }}>
								{t('UI_TEXT.MACHINE_MODEL')} ({machineModels.length})
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							{machineModels.length > 0 ? (
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "center",
										alignItems: "center",
										columnGap: "15px",
										flexWrap: "wrap",
									}}>
									{machineModels.map((item) => (
										<MachineCard
											key={item.filename}
											showOnlyModelIcons
											highlighted={item.containsPart}
											cardId={cardDomId(item)}
											subtitleContent={renderModelSubtitle(item, t)}
											product={{
												...modelToMachineCardProduct(item, {
													machine_id,
													machine_name,
													machine_number,
													productNumbers,
												}),
												forcedModel: item, // 👈 IMPORTANT
											}}
										/>
									))}



								</Box>
							) : (
								<Box sx={{ textAlign: "center", py: 3 }}>
									<Typography variant="body1" color="text.secondary">
										{t('UI_TEXT.NO_MACHINE_MODELS_FOUND')}
									</Typography>
								</Box>
							)}
						</AccordionDetails>
					</Accordion>

					{/* Split Files Section */}
					<Accordion
						sx={{
							width: "100%",
							backgroundColor: alpha(theme.palette.background.paper, 1),
							borderRadius: 2,
							boxShadow: "none",
						}}
						expanded={expandedPanels.split}
						onChange={handleAccordionChange("split")}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}
							sx={{
								px: 2,
								pr: 2,
								...(expandedPanels.bom && {
									position: "sticky",
									top: 0,
									zIndex: 3,
									backgroundColor: alpha(theme.palette.background.paper, 1),
								})
							}}
						>
							<Typography variant="h6" sx={{ fontWeight: "bold" }}>
								{t('UI_TEXT.SPLIT_FILE')} ({splitModels.length})
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							{splitModels.length > 0 ? (
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "center",
										alignItems: "center",
										columnGap: "15px",
										flexWrap: "wrap",
									}}>
									{splitModels.map((item) => (
										<MachineCard
											key={item.filename}
											showOnlyModelIcons
											highlighted={item.containsPart}
											cardId={cardDomId(item)}
											subtitleContent={renderModelSubtitle(item, t)}
											product={{
												...modelToMachineCardProduct(item, {
													machine_id,
													machine_name,
													machine_number,
													productNumbers,
												}),
												forcedModel: item, // 👈 IMPORTANT
											}}
										/>
									))}


								</Box>
							) : (
								<Box sx={{ textAlign: "center", py: 3 }}>
									<Typography variant="body1" color="text.secondary">
										{t('UI_TEXT.NO_SPLIT_MODELS_FOUND')}
									</Typography>
								</Box>
							)}
						</AccordionDetails>
					</Accordion>

					{/* BOM Models Section */}
					<Accordion
						sx={{
							mb: 2, width: "100%",
							backgroundColor: alpha(theme.palette.background.paper, 1),
							borderRadius: 2,
							boxShadow: "none",
						}}
						expanded={expandedPanels.bom}
						onChange={handleAccordionChange("bom")}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}
							sx={{
								px: 2,
								pr: 2,
								...(expandedPanels.bom && {
									position: "sticky",
									top: 0,
									zIndex: 3,
									backgroundColor: alpha(theme.palette.background.paper, 1),
								})
							}}
						>
							<Typography variant="h6" sx={{ fontWeight: "bold" }}>
								Sub-assemblies (BOM) ({bomModels.length})
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							{bomModels.length > 0 ? (
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "center",
										alignItems: "center",
										columnGap: "15px",
										flexWrap: "wrap",
									}}>
									{bomModels.map((item) => (
										<MachineCard
											key={item.filename}
											showOnlyModelIcons
											highlighted={item.containsPart}
											cardId={cardDomId(item)}
											subtitleContent={renderModelSubtitle(item, t)}
											product={{
												...modelToMachineCardProduct(item, {
													machine_id,
													machine_name,
													machine_number,
													productNumbers,
												}),
												forcedModel: item, // 👈 IMPORTANT
											}}
										/>
									))}


								</Box>
							) : (
								<Box sx={{ textAlign: "center", py: 3 }}>
									<Typography variant="body1" color="text.secondary">
										{t('UI_TEXT.NO_SUB_ASSEMBLY_MODEL')}
									</Typography>
								</Box>
							)}
						</AccordionDetails>
					</Accordion>
				</Box>

				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						alignItems: "center",
						mb: 2,
						mt: 1,
						width: "100%",
					}}>
					<Button
						variant="outlined"
						onClick={(e) => {
							e.stopPropagation();
							handleCloseDialog();
						}}>
						{t("BUTTON.CLOSE")}
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default MultipleModelViewer;
